import { memo, useRef, useMemo, useCallback, useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import { ActionType, LenderValidationErrors, UsageResponse } from '@pmp/api';

import { getLenderFormSchema } from '../formConfig';
import type { LenderDetailsFormProps, LenderRequest } from '../interfaces';
import { crudToActionMap, getCrudMode, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';

type LenderCrudChangeRequest = {
  data: LenderRequest;
  actionType: ActionType;
  dryRun?: boolean;
};

type LenderChangesCreateOrValidateChangeRequestResponse = {
  isValid: boolean;
  errors: LenderValidationErrors;
};

const pmpFake = {
  lenderCheckUsage: ({ path: { id } }: { path: { id: string | undefined } }) => Promise.resolve({ data: { isUsed: false } }),
  lenderRuleChangesCreateOrValidateChangeRequest: ({ body }: { body: LenderCrudChangeRequest }) =>
    Promise.resolve({ data: { isValid: true, errors: {} } }),
};

export const LenderCrudForm = memo(({ mode, record, onClose, refreshTable }: LenderDetailsFormProps): ReactElement | null => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  useEffect(() => {
    if (isUsed !== null) return;
    if (mode !== CrudMode.EDIT) {
      setIsUsed(false);
      setOpen(true);
      return;
    }

    setOpen(true);
    if (!record?.name) {
      notification().error({ message: t('global.errors.unexpected') });
      onClose();
      return;
    }
    pmpFake
      .lenderCheckUsage({ path: { id: record.id } })
      .then(({ data: { isUsed } }: { data: UsageResponse }) => {
        setIsUsed(isUsed);
        setOpen(true);
      })
      .catch(() => {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
      });
  }, [mode, record, isUsed, t, onClose]);

  const onConfirm: (formData: LenderRequest) => Promise<ValidationError[] | void> = useCallback(
    ({ name, ...values }) => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

      const body: LenderCrudChangeRequest = {
        data: {
          ...record,
          address: {
            ...values.address,
            lastUpdated: dayjs().toISOString(),
            deleted: false,
          },
        } as LenderRequest,
        actionType: crudToActionMap[mode],
      };
      return pmpFake
        .lenderRuleChangesCreateOrValidateChangeRequest({ body })
        .then(({ data: { isValid, errors } }: { data: LenderChangesCreateOrValidateChangeRequestResponse }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }

            return transformCrudFormErrors(errors);
          }
          notification().success({
            message: changeRequestNotificationMessage(t, enableFourEyes),
          });
          refreshTable();
        });
    },
    [mode, refreshTable, record, t, enableFourEyes],
  );

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const title = t('global.labels.editX', { x: t('lender.lender.title') });

  const initialValues: LenderRequest = useMemo(() => {
    return record as LenderRequest;
  }, [record]);

  const crudProps: CrudProps<LenderRequest> = useMemo(() => {
    return {
      ref,
      open,
      schema: getLenderFormSchema(mode),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues,
      mode: getCrudMode(mode, isUsed),
      onClose: handleOnClose,
      onConfirm,
    };
  }, [open, mode, title, t, initialValues, handleOnClose, onConfirm, isUsed, enableFourEyes]);

  if (isUsed === null) return null;

  return <Crud {...crudProps} />;
});
